import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Box from "@amzn/meridian/esm/components/box";
import Row from "@amzn/meridian/esm/components/row";
import Theme from "@amzn/meridian/esm/components/theme/theme";
import HelpTabInClient from "./helpTab/HelpTabInClient";
import SummariesTabInClient from "./summaryTab/SummariesTabInClient";
import SidebarInClient from "./common/SidebarInClient";
import { fetchPaginatedSummaries } from "../utils/SummaryUtil";
import LogManager from "../utils/LogManager";

/**
 * MainClientHome Component
 *
 * This component serves as the main entry UI for users in the "MainClient" mode.
 * It features a side menu with two selectable tabs: "Summaries" and "Help".
 *
 * Functionality:
 * - When "Summaries" is selected, it displays the SummariesTabInClient component,
 *   showing the list of meeting summaries or related content.
 * - When "Help" is selected, it renders the HelpTabInClient component,
 *   which includes a FeedbackCard for user feedback, a WikiCard for documentation or assistance and a PolicyCard to understand Amazon policy on using AI in meetings.
 *
 * The component uses state to manage the selected tab and dynamically renders
 * the appropriate content based on the user's selection.
 */
const MainClientHome = () => {
  const location = useLocation();
  const initialTab = location.state?.selectedTab || "summaries";
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const returnPage = location.state?.returnPage ?? 1;
  const [isPrefetchComplete, setIsPrefetchComplete] = useState(false);

  console.log('Document Visibility State:', document.visibilityState);

  // Prefetch summaries data on component mount (like Home.jsx pattern)
  useEffect(() => {
    // Skip API call for localhost
    if (window.location.href.includes("http://localhost:3000/")) {
      setIsPrefetchComplete(true); // Mark as complete for localhost
      return;
    }

    // Always fetch fresh data from backend on mount/refresh
    fetchPaginatedSummaries(true, null, 10, {
      setSummaryGroups: () => {}, // These will be handled by the utility function
      setRawSummaries: () => {},
      setLastEvaluatedKey: () => {},
      setIsEndOfData: () => {}
    })
    .then(result => {
      setIsPrefetchComplete(true);
    })
    .catch(error => {
      LogManager.error("Failed to prefetch summaries in MainClientHome:", error);
      setIsPrefetchComplete(true);
    });
  }, []);

  return (
    <Row spacing="none" spacingInset="none" alignmentVertical="top">
      <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
        <Box type="outline" spacing="none" spacingInset="none" minHeight={557} minWidth={176}>
          <SidebarInClient selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
        </Box>
      </Theme>

      {selectedTab === "help" && <HelpTabInClient />}
      {selectedTab === "summaries" && (
        <>
          <SummariesTabInClient returnPage={returnPage} isPrefetchComplete={isPrefetchComplete} />
        </>
      )}
    </Row>
  );
};

export default MainClientHome;
